import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

interface Lottery {
  lotteryId: string;
  name: string;
  description?: string;
  status: 'active' | 'ended' | 'pending';
  drawDate: string;
  registrationOpens: string;
  registrationCloses?: string;
  totalPrizes: number;
  canJoin: boolean;
  alreadyEntered: boolean;
  requiresGPS: boolean;
  requiresActivation: boolean;
  maxEntriesPerTag: number;
}

interface Props {
  isOpen: boolean;
  // list mode OR single-detail mode
  lotteries?: Lottery[] | null;
  lottery?: Lottery | null;
  onClose: () => void;
  onJoin: (lottery: Lottery) => void;
  isLoading?: boolean;
}

const safeFormatDate = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return d.toDateString();
  }
};

const LotteryModal: React.FC<Props> = ({ isOpen, lotteries = null, lottery = null, onClose, onJoin, isLoading = false }) => {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Effects must run unconditionally; they internally guard on isOpen.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => closeButtonRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(
            'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        ).filter(el => el.offsetParent !== null);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, isLoading, onClose]);

  // If modal shouldn't render (closed or no content), return null
  if (!isOpen || (!lottery && (!Array.isArray(lotteries) || lotteries.length === 0))) return null;

  const onBackdropClick = (e: React.MouseEvent) => {
    if (isLoading) return;
    if (e.target === e.currentTarget) onClose();
  };

  const renderSingle = (l: Lottery) => {
    const joinDisabled = isLoading || !l.canJoin || l.alreadyEntered || l.status !== 'active';
    return (
      <>
        <div className="text-center mb-6">
          <div className="text-6xl mb-3" aria-hidden>🎰</div>
          <h2 id="lottery-title" className="text-2xl font-bold text-gray-900 mb-2">
            {l.alreadyEntered ? 'Lottery Entry' : 'Join Lottery?'}
          </h2>
          <h3 className="text-lg font-semibold text-purple-600">{l.name}</h3>
        </div>

        <div className="space-y-4 mb-6">
          {l.description && <p id="lottery-desc" className="text-gray-600 text-sm text-center">{l.description}</p>}

          <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-600">Total Prizes:</span><span className="font-semibold">{l.totalPrizes}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Draw Date:</span><span className="font-semibold">{safeFormatDate(l.drawDate)}</span></div>
            {l.registrationCloses && <div className="flex justify-between"><span className="text-gray-600">Registration Closes:</span><span className="font-semibold">{safeFormatDate(l.registrationCloses)}</span></div>}
            <div className="flex justify-between"><span className="text-gray-600">Max Entries:</span><span className="font-semibold">{l.maxEntriesPerTag} per tag</span></div>
          </div>

          {(l.requiresGPS || l.requiresActivation) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="text-xs font-semibold text-blue-800 mb-2">Requirements:</div>
              <ul className="text-xs text-blue-700 space-y-1">
                {l.requiresActivation && <li>✓ Product must be activated</li>}
                {l.requiresGPS && <li>✓ GPS location required</li>}
              </ul>
            </div>
          )}

          {l.status !== 'active' && (
            <div className="text-sm text-gray-700 bg-yellow-50 border border-yellow-100 rounded p-2">
              {l.status === 'ended' ? 'This lottery has ended.' : 'This lottery is not open yet.'}
            </div>
          )}

          {l.alreadyEntered && (
            <div className="text-sm text-gray-700 bg-green-50 border border-green-100 rounded p-2">You have already entered this lottery.</div>
          )}
        </div>

        <div className="flex gap-3">
          <button type="button" onClick={onClose} disabled={isLoading}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50">
            Cancel
          </button>

          <button type="button" onClick={() => onJoin(l)} disabled={joinDisabled}
            aria-disabled={joinDisabled}
            className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 ${
              joinDisabled ? 'bg-gray-200 text-gray-600' : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700'
            }`}>
            {isLoading ? (
              <span className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />Joining...
              </span>
            ) : l.alreadyEntered ? '🎲 View Entry' : l.status !== 'active' ? 'Not Available' : '🎲 Join Now'}
          </button>
        </div>
      </>
    );
  };

  const renderList = (list: Lottery[]) => (
    <>
      <div className="text-center mb-4">
        <div className="text-4xl mb-2" aria-hidden>🎰</div>
        <h2 className="text-xl font-bold text-gray-900">Available Lotteries</h2>
        <div className="text-sm text-gray-500 mt-1">{list.length} found</div>
      </div>

      <div className="space-y-3 max-h-[60vh] overflow-y-auto mb-4">
        {list.map(l => (
          <div key={l.lotteryId} className="p-3 border rounded flex items-start justify-between">
            <div>
              <div className="font-medium">{l.name}</div>
              {l.description && <div className="text-sm text-gray-500">{l.description}</div>}
              <div className="text-xs mt-2 text-gray-400">Draw: {safeFormatDate(l.drawDate)}</div>
            </div>
            <div className="flex flex-col items-end gap-2">
              {l.status === 'active' && l.canJoin ? (
                <button type="button" onClick={() => onJoin(l)} className="px-4 py-2 bg-green-600 text-white rounded">Join</button>
              ) : (
                <div className="text-sm text-gray-500">{l.status === 'active' ? 'Not eligible' : (l.status === 'ended' ? 'Ended' : 'Not open')}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end">
        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 rounded">Close</button>
      </div>
    </>
  );

  const body = Array.isArray(lotteries) && lotteries.length > 0
    ? renderList(lotteries)
    : (lottery ? renderSingle(lottery) : null);

  const modal = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2000] p-4" role="presentation" onMouseDown={onBackdropClick}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="lottery-title" aria-describedby="lottery-desc"
        className="bg-white rounded-2xl max-w-md w-full p-6 relative shadow-lg" onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" ref={closeButtonRef} onClick={onClose} aria-label="Close lottery modal"
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-300 rounded"
          disabled={isLoading}>
          ✕
        </button>

        {body}

        <p className="text-xs text-gray-500 text-center mt-4">By joining, you agree to the lottery terms and conditions.</p>
      </div>
    </div>
  );

  // Render into document.body to avoid stacking-context issues
  return typeof document !== 'undefined' ? ReactDOM.createPortal(modal, document.body) : modal;
};

export default LotteryModal;